import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../axiosInstance";

const safeProfileCompletion = (payloadProfileCompletion) => ({
  ...initialState.profileCompletion,
  ...(payloadProfileCompletion || {}),
});


const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  adminAllUsers: [],
  profileCompletion: {
    percentage: 0,
    missingFields: [],
    sectionCompletion: {},
    isProfileComplete: false,
    nextSteps: [],
  },
  operations: {
    profileUpdate: { loading: false, success: false, error: null },
    passwordUpdate: { loading: false, success: false, error: null },
    forgotPassword: {
      loading: false,
      success: false,
      error: null,
      message: null,
    },
    otpVerification: {
      loading: false,
      success: false,
      error: null,
      token: null,
    },
    imageUpload: { loading: false, success: false, error: null },
    imageDelete: { loading: false, success: false, error: null },
  },
};

// Async Thunks
export const userRegister = createAsyncThunk(
  "user/register",
  async (userData, { rejectWithValue }) => {
    try {
      const { data } = await axios.post("/api/v1/auth/register", userData);
      if (!data.token) {
        return rejectWithValue("No token returned from server");
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      localStorage.setItem("refreshToken", data.refreshToken);

      return {
        user: data.user,
        token: data.token,
        profileCompletion: data.profileCompletion || initialState.profileCompletion
      };
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || "Registration failed";
      return rejectWithValue(message);
    }
  }
);

export const googleRegister = createAsyncThunk(
  "user/googleRegister",
  async (tokenId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post("/api/v1/auth/google-register", {
        tokenId,
      });

      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      localStorage.setItem("refreshToken", data.refreshToken);

      return {
        user: data.user,
        token: data.token,
        profileCompletion: data.profileCompletion || initialState.profileCompletion
      };
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "Google registration failed";
      return rejectWithValue(message);
    }
  }
);

export const login = createAsyncThunk(
  "user/login",
  async ({ email, phone, password }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post("/api/v1/auth/login", {
        email,
        phone,
        password,
      });

      if (!data.token) {
        return rejectWithValue("No token returned from server");
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      localStorage.setItem("refreshToken", data.refreshToken);

      return {
        user: data.user,
        token: data.token,
        profileCompletion: data.profileCompletion || initialState.profileCompletion
      };
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || "Login failed";
      return rejectWithValue(message);
    }
  }
);

export const googleLogin = createAsyncThunk(
  "user/googleLogin",
  async (tokenId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post("/api/v1/auth/google-login", {
        tokenId,
      });

      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      localStorage.setItem("refreshToken", data.refreshToken);

      return {
        user: data.user,
        token: data.token,
        profileCompletion: data.profileCompletion || initialState.profileCompletion
      };
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || "Google login failed";
      return rejectWithValue(message);
    }
  }
);

export const reloadUser = createAsyncThunk(
  "user/reload",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get("/api/v1/auth/profile");
      localStorage.setItem("profileCompletion", JSON.stringify(data.profileCompletion));
      return {
        user: data.user,
        profileCompletion: data.profileCompletion || initialState.profileCompletion
      };
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || "Profile reload failed";
      return rejectWithValue(message);
    }
  }
);

export const logout = createAsyncThunk(
  "user/logout",
  async (_, { rejectWithValue }) => {
    try {
      await axios.post("/api/v1/auth/logout");
      return true;
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || "Logout failed";
      return rejectWithValue(message);
    }
  }
);

export const updateProfile = createAsyncThunk(
  "user/updateProfile",
  async (userData, { rejectWithValue }) => {
    try {
      const { data } = await axios.put("/api/v1/auth/profile/update", userData);
      
      // Fetch updated profile with completion data
      const profileRes = await axios.get("/api/v1/auth/profile");
      
      return {
        success: data.success,
        user: profileRes.data.user,
        profileCompletion: profileRes.data.profileCompletion || initialState.profileCompletion
      };
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || "Profile update failed";
      return rejectWithValue(message);
    }
  }
);

export const updatePassword = createAsyncThunk(
  "user/updatePassword",
  async (passwords, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        "/api/v1/auth/profile/password/update",
        passwords
      );
      return data.success;
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || "Password update failed";
      return rejectWithValue(message);
    }
  }
);

export const forgotPassword = createAsyncThunk(
  "user/forgotPassword",
  async ({ email }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post("/api/v1/auth/password/forgot", {
        email,
      });
      return data.message;
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || "Forgot password failed";
      return rejectWithValue(message);
    }
  }
);

export const verifyOtp = createAsyncThunk(
  "user/verifyOtp",
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post("/api/v1/auth/verify/otp", {
        email,
        otp,
      });
      return data;
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || "OTP verification failed";
      return rejectWithValue(message);
    }
  }
);

export const resetPassword = createAsyncThunk(
  "user/resetPassword",
  async ({ passwords }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        "/api/v1/auth/password/reset",
        passwords
      );
      return data.success;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateUserMode = createAsyncThunk(
  "user/updateUserMode",
  async ({ id, mode }, { rejectWithValue }) => {
    try {
      const { data } = await axios.patch(`/api/v1/auth/update-mode/${id}`, {
        mode,
      });

      const updatedUser = {
        ...JSON.parse(localStorage.getItem("user")),
        mode: data.data.mode,
      };
      localStorage.setItem("user", JSON.stringify(updatedUser));

      return data.data;
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "Failed to update user mode";
      return rejectWithValue(message);
    }
  }
);

export const getAllUsers = createAsyncThunk(
  "admin/getAllUsers",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get("/api/v1/auth/admin/all/users");
      if (!data.users || !Array.isArray(data.users)) {
        return rejectWithValue("No users found");
      }
      return data.users;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch users"
      );
    }
  }
);

export const deleteUser = createAsyncThunk(
  "admin/deleteUser",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.delete(`/api/v1/auth/admin/user/${id}`);
      if (!data.success) {
        return rejectWithValue(data.message || "Failed to delete user");
      }
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to delete user"
      );
    }
  }
);

export const uploadProfileImage = createAsyncThunk(
  "user/uploadProfileImage",
  async (formData, { rejectWithValue }) => {
    try {
      const { data } = await axios.put("/api/v1/auth/profile/upload-image", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      
      // Fetch updated profile with completion data
      const profileRes = await axios.get("/api/v1/auth/profile");
      
      return {
        imageData: data,
        user: profileRes.data.user,
        profileCompletion: profileRes.data.profileCompletion || initialState.profileCompletion
      };
    } catch (err) {
      const message = err.response?.data?.message || err.message || "Image upload failed";
      return rejectWithValue(message);
    }
  }
);

export const deleteProfileImage = createAsyncThunk(
  "user/deleteProfileImage",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.delete("/api/v1/auth/profile/delete-image");
      
      // Fetch updated profile with completion data
      const profileRes = await axios.get("/api/v1/auth/profile");
      
      return {
        deleteData: data,
        user: profileRes.data.user,
        profileCompletion: profileRes.data.profileCompletion || initialState.profileCompletion
      };
    } catch (err) {
      const message = err.response?.data?.message || err.message || "Image deletion failed";
      return rejectWithValue(message);
    }
  }
);

export const loginSendOtp = createAsyncThunk(
  "user/loginSendOtp",
  async ({ phone }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post("/api/v1/auth/login-send-otp", {
        phone,
      });
      return data.message;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to send OTP"
      );
    }
  }
);

export const loginVerifyOtp = createAsyncThunk(
  "user/loginVerifyOtp",
  async ({ phone, otp }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post("/api/v1/auth/login-verify-otp", {
        phone,
        otp,
      });

      // 🔥 Token store karo
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      localStorage.setItem("refreshToken", data.refreshToken);

      return {
        user: data.user,
        token: data.token,
        profileCompletion: data.profileCompletion || initialState.profileCompletion
      };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "OTP verification failed"
      );
    }
  }
);


const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    clearErrors(state) {
      state.error = null;
      Object.values(state.operations).forEach((op) => {
        op.error = null;
      });
    },
    resetOperation(state, action) {
      const opName = action.payload;
      if (!state.operations[opName]) return;
      state.operations[opName] = {
        loading: false,
        success: false,
        error: null,
      };
      if (opName === "forgotPassword")
        state.operations.forgotPassword.message = null;
      if (opName === "otpVerification")
        state.operations.otpVerification.token = null;
    },
    resetProfileUpdate(state) {
      state.operations.profileUpdate = {
        loading: false,
        success: false,
        error: null,
      };
    },
    resetPasswordUpdate(state) {
      state.operations.passwordUpdate = {
        loading: false,
        success: false,
        error: null,
      };
    },
    resetImageUpload(state) {
      state.operations.imageUpload = {
        loading: false,
        success: false,
        error: null,
      };
    },
    resetImageDelete(state) {
      state.operations.imageDelete = {
        loading: false,
        success: false,
        error: null,
      };
    },
    hydrateUserFromStorage(state, { payload }) {
      state.user = payload.user || null;
      state.token = payload.token || null;
      state.isAuthenticated = !!payload.token;
    },
    refreshTokenUserFromStorage(state, { payload }) {
      state.user = payload.user || null;
      state.refreshToken = payload.refreshToken || null;
      state.isAuthenticated = !!payload.refreshToken;
    },
    // New reducers for profile completion
    updateProfileCompletion(state, { payload }) {
      if (payload.percentage !== undefined) {
        state.profileCompletion.percentage = payload.percentage;
      }
      if (payload.missingFields) {
        state.profileCompletion.missingFields = payload.missingFields;
      }
      if (payload.sectionCompletion) {
        state.profileCompletion.sectionCompletion = payload.sectionCompletion;
      }
      if (payload.isProfileComplete !== undefined) {
        state.profileCompletion.isProfileComplete = payload.isProfileComplete;
      }
      if (payload.nextSteps) {
        state.profileCompletion.nextSteps = payload.nextSteps;
      }
    },
    calculateProfileCompletion(state) {
      if (!state.user) return;
      
      const user = state.user;
      let totalFields = 0;
      let completedFields = 0;
      const missingFields = [];

      // Basic fields
      const basicFields = ["name", "email", "phone"];
      basicFields.forEach(field => {
        totalFields++;
        if (user[field] && user[field].toString().trim() !== "") {
          completedFields++;
        } else {
          missingFields.push(field.charAt(0).toUpperCase() + field.slice(1));
        }
      });

      // Business fields
      const businessFields = ["businessName", "businessAddress"];
      businessFields.forEach(field => {
        totalFields++;
        if (user[field] && user[field].toString().trim() !== "") {
          completedFields++;
        } else {
          missingFields.push(field === "businessName" ? "Business Name" : "Business Address");
        }
      });

      // Seller specific
      if (user.mode === "seller") {
        totalFields++;
        if (user.gstNumber && user.gstNumber.trim() !== "") {
          completedFields++;
        } else {
          missingFields.push("GST Number");
        }
      }

      // Profile image
      totalFields++;
      if (user.profileImage && user.profileImage.url) {
        completedFields++;
      } else {
        missingFields.push("Profile Picture");
      }

      // Phone verification
      totalFields++;
      if (user.phoneVerified) {
        completedFields++;
      } else {
        missingFields.push("Phone Verification");
      }

      // Email verification
      totalFields++;
      if (user.emailVerified) {
        completedFields++;
      } else {
        missingFields.push("Email Verification");
      }

      // Calculate percentage
      const percentage = Math.round((completedFields / totalFields) * 100);
      
      state.profileCompletion = {
        percentage,
        missingFields,
        sectionCompletion: {},
        isProfileComplete: percentage >= 80,
        nextSteps: missingFields.slice(0, 3),
      };
    }
    
  },

  
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.user = payload.user;
        state.token = payload.token;
        state.profileCompletion = payload.profileCompletion || initialState.profileCompletion;
        state.isAuthenticated = true;
      })
      .addCase(login.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      })

      // Google Register
      .addCase(googleRegister.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(googleRegister.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.user = payload.user;
        state.token = payload.token;
        state.profileCompletion = payload.profileCompletion || initialState.profileCompletion;
        state.isAuthenticated = true;
      })
      .addCase(googleRegister.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      })

      // Google login
      .addCase(googleLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(googleLogin.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.user = payload.user;
        state.token = payload.token;
        state.profileCompletion = payload.profileCompletion || initialState.profileCompletion;
        state.isAuthenticated = true;
      })
      .addCase(googleLogin.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      })

      // Reload User
      .addCase(reloadUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // .addCase(reloadUser.fulfilled, (state, { payload }) => {
      //   state.loading = false;
      //   state.user = payload.user;
      //   state.profileCompletion = payload.profileCompletion;
      //   state.isAuthenticated = true;
      // })
      .addCase(reloadUser.fulfilled, (state, { payload }) => {
        state.loading = false;
  state.user = payload.user;
state.isAuthenticated = true;
  if (payload.profileCompletion?.percentage !== undefined) {
    state.profileCompletion = payload.profileCompletion;
  } else {
    // fallback only
    userSlice.caseReducers.calculateProfileCompletion(state);
  }
})
      .addCase(reloadUser.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
        state.isAuthenticated = false;
      })

      // Logout
      .addCase(logout.fulfilled, () => initialState)
      .addCase(logout.rejected, (state, { payload }) => {
        state.error = payload;
      })

      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.operations.profileUpdate.loading = true;
        state.operations.profileUpdate.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, { payload }) => {
        state.operations.profileUpdate.loading = false;
        state.operations.profileUpdate.success = payload.success;
        state.user = payload.user;
        // state.profileCompletion = payload.profileCompletion;
        state.profileCompletion = safeProfileCompletion(payload.profileCompletion);
      })
      .addCase(updateProfile.rejected, (state, { payload }) => {
        state.operations.profileUpdate.loading = false;
        state.operations.profileUpdate.error = payload;
      })

      // Update Password
      .addCase(updatePassword.pending, (state) => {
        state.operations.passwordUpdate.loading = true;
        state.operations.passwordUpdate.error = null;
      })
      .addCase(updatePassword.fulfilled, (state, { payload }) => {
        state.operations.passwordUpdate.loading = false;
        state.operations.passwordUpdate.success = payload;
      })
      .addCase(updatePassword.rejected, (state, { payload }) => {
        state.operations.passwordUpdate.loading = false;
        state.operations.passwordUpdate.error = payload;
      })

      // Forgot Password
      .addCase(forgotPassword.pending, (state) => {
        state.operations.forgotPassword.loading = true;
        state.operations.forgotPassword.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state, { payload }) => {
        state.operations.forgotPassword.loading = false;
        state.operations.forgotPassword.success = true;
        state.operations.forgotPassword.message = payload;
      })
      .addCase(forgotPassword.rejected, (state, { payload }) => {
        state.operations.forgotPassword.loading = false;
        state.operations.forgotPassword.error = payload;
      })

      // Verify OTP
      .addCase(verifyOtp.pending, (state) => {
        state.operations.otpVerification.loading = true;
        state.operations.otpVerification.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, { payload }) => {
        state.operations.otpVerification.loading = false;
        state.operations.otpVerification.success = true;
        state.operations.otpVerification.token = payload.resetPasswordToken;
      })
      .addCase(verifyOtp.rejected, (state, { payload }) => {
        state.operations.otpVerification.loading = false;
        state.operations.otpVerification.error = payload;
      })

      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.operations.passwordUpdate.success = payload;
      })
      .addCase(resetPassword.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })

      // Update User Mode
      .addCase(updateUserMode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserMode.fulfilled, (state, { payload }) => {
        state.loading = false;
        if (state.user && state.user._id === payload._id) {
          state.user.mode = payload.mode;
        }
      })
      .addCase(updateUserMode.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })

      // Get All Users (Admin)
      .addCase(getAllUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.adminAllUsers = action.payload;
      })
      .addCase(getAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete User
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        if (state.adminAllUsers) {
          state.adminAllUsers = state.adminAllUsers.filter(
            (user) => user._id !== action.payload
          );
        }
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Upload Profile Image
      .addCase(uploadProfileImage.pending, (state) => {
        state.operations.imageUpload.loading = true;
        state.operations.imageUpload.success = false;
        state.operations.imageUpload.error = null;
      })
      .addCase(uploadProfileImage.fulfilled, (state, { payload }) => {
        state.operations.imageUpload.loading = false;
        state.operations.imageUpload.success = true;
        state.user = payload.user;
        // state.profileCompletion = payload.profileCompletion;
        state.profileCompletion = safeProfileCompletion(payload.profileCompletion);
      })
      .addCase(uploadProfileImage.rejected, (state, { payload }) => {
        state.operations.imageUpload.loading = false;
        state.operations.imageUpload.error = payload;
      })

      // Delete Profile Image
      .addCase(deleteProfileImage.pending, (state) => {
        state.operations.imageDelete.loading = true;
        state.operations.imageDelete.success = false;
        state.operations.imageDelete.error = null;
      })
      .addCase(deleteProfileImage.fulfilled, (state, { payload }) => {
        state.operations.imageDelete.loading = false;
        state.operations.imageDelete.success = true;
        state.user = payload.user;
        // state.profileCompletion = payload.profileCompletion;
         state.profileCompletion = safeProfileCompletion(payload.profileCompletion);
      })
      .addCase(deleteProfileImage.rejected, (state, { payload }) => {
        state.operations.imageDelete.loading = false;
        state.operations.imageDelete.error = payload;
      })

      // SEND OTP
.addCase(loginSendOtp.pending, (state) => {
  state.operations.otpVerification.loading = true;
  state.operations.otpVerification.error = null;
})
.addCase(loginSendOtp.fulfilled, (state) => {
  state.operations.otpVerification.loading = false;
})
.addCase(loginSendOtp.rejected, (state, { payload }) => {
  state.operations.otpVerification.loading = false;
  state.operations.otpVerification.error = payload;
})

// VERIFY OTP (LOGIN SUCCESS)
.addCase(loginVerifyOtp.pending, (state) => {
  state.operations.otpVerification.loading = true;
  state.operations.otpVerification.error = null;
})
.addCase(loginVerifyOtp.fulfilled, (state, { payload }) => {
  state.operations.otpVerification.loading = false;
  state.operations.otpVerification.success = true;
  state.user = payload.user;
  state.token = payload.token;
  state.isAuthenticated = true;
})
.addCase(loginVerifyOtp.rejected, (state, { payload }) => {
  state.operations.otpVerification.loading = false;
  state.operations.otpVerification.error = payload;
})

  },
});

export const {
  clearErrors,
  resetOperation,
  resetProfileUpdate,
  resetPasswordUpdate,
  resetImageUpload,
  resetImageDelete,
  hydrateUserFromStorage,
  refreshTokenUserFromStorage,
  updateProfileCompletion,
  calculateProfileCompletion,
} = userSlice.actions;

export default userSlice.reducer;